'use client';

import { Device } from '@coolblue-development/next-request-context';
import { useRequestContext } from '@coolblue-development/next-request-context/client';
import { useTranslation } from '@i18n/useTranslation';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { createContext, type ReactNode, useState, useTransition } from 'react';

import type { Slug } from '../components/Sorting/slugs';
import { getSortingQueryParameterKey } from '../utils';
import { useUpdatePath, useUpdateQueryString } from './hooks';
import {
  FilterPageContextProps,
  filterPageTrackingSubFeature,
  PresentationalFilterFacet
} from './types';

const initialState: FilterPageContextProps = {
  filters: [],
  initialFilters: [],
  isPending: false,
  path: '',
  trackingSubFeature: 'filter_list'
};

const FilterPageContext = createContext(initialState);

const FilterPageStore = ({
  children,
  filters,
  path,
  trackingSubFeature
}: {
  children: ReactNode;
  filters: PresentationalFilterFacet[];
  path: string;
  trackingSubFeature: filterPageTrackingSubFeature;
}) => {
  return (
    <FilterPageContextProvider
      initialFilters={filters}
      path={path}
      trackingSubFeature={trackingSubFeature}
    >
      {children}
    </FilterPageContextProvider>
  );
};

const FilterPageContextProvider = ({
  children,
  initialFilters = [],
  onFilterValueChange,
  path = '',
  trackingSubFeature
}: FilterPageContextProps & { children: ReactNode }) => {
  const [isPending, startTransition] = useTransition();
  const [filters, setFilters] = useState<PresentationalFilterFacet[]>(() => initialFilters);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sorting, setSorting] = useState<Slug>('relevance');

  const { language, environment, device } = useRequestContext();
  const isMobile = device === Device.MOBILE;
  const isDevelopment = environment === 'development';
  const router = useRouter();
  const searchParams = useSearchParams();
  const { t } = useTranslation('webshop');
  const pathname = usePathname();
  const { updatePath } = useUpdatePath(setPath, pathname || '', isDevelopment);
  const { updateQueryString } = useUpdateQueryString({
    filters,
    path,
    pathname: pathname || '',
    setPath,
    isDevelopment
  });

  const sortingQueryParameterKey = getSortingQueryParameterKey(language);
  const pageQueryParameterKey = t('filterPage.page').toLowerCase();

  function setPath(pathname: string, shouldScroll = false) {
    const shouldScrollBasedOnDevice = isMobile ? true : shouldScroll;
    startTransition(() => {
      router.push(pathname, { scroll: shouldScrollBasedOnDevice });
    });
  }

  function getFilter(name: string) {
    return filters.find(filter => filter.name === name);
  }

  function setFilter(
    filterInput: PresentationalFilterFacet | PresentationalFilterFacet[],
    shouldScroll = false
  ) {
    const filterArray = Array.isArray(filterInput) ? filterInput : [filterInput];

    const newFilters = filterArray.reduce<PresentationalFilterFacet[]>(
      (acc, newFilter) => {
        const filteredAcc = acc.filter(filter => filter.name !== newFilter.name);
        return [...filteredAcc, newFilter];
      },
      [...filters]
    );

    const newFiltersWithProducts = newFilters.filter(
      filter => filter.values.filter(String).length > 0
    );

    setFilters(newFiltersWithProducts);

    if (onFilterValueChange) {
      onFilterValueChange({ filters: newFiltersWithProducts, currentPage });
    } else {
      const params = new URLSearchParams(searchParams?.toString() || '');
      params.delete(pageQueryParameterKey);

      updatePath?.(path, newFiltersWithProducts, params, shouldScroll);
    }
  }

  function clearFilters() {
    setFilters([]);

    if (onFilterValueChange) {
      onFilterValueChange({ filters: [], currentPage });
    } else {
      const params = new URLSearchParams(searchParams?.toString() || '');
      params.delete(pageQueryParameterKey);

      updatePath?.(path, [], params, false);
    }
  }

  function updateCurrentPage(page: number, callback?: () => void) {
    setCurrentPage(page);

    if (onFilterValueChange) {
      onFilterValueChange({ filters, currentPage: page });
    } else {
      updateQueryString(
        { name: pageQueryParameterKey, ...(page > 1 && { value: String(page) }) },
        callback
      );
    }
  }

  function updateSorting(sortBy: Slug, callback?: () => void) {
    setSorting(sortBy);

    if (onFilterValueChange) {
      onFilterValueChange({ filters, currentPage });
    } else {
      updateQueryString(
        [{ name: pageQueryParameterKey }, { name: sortingQueryParameterKey, value: sortBy }],
        callback
      );
    }
  }

  return (
    <FilterPageContext.Provider
      value={{
        clearFilters,
        currentPage,
        filters,
        getFilter,
        isPending,
        path,
        setFilter,
        setPath,
        sorting,
        updateCurrentPage,
        updateSorting,
        trackingSubFeature
      }}
    >
      {children}
    </FilterPageContext.Provider>
  );
};

export { FilterPageContext, FilterPageContextProvider, FilterPageStore };
